import { IVocTreeEdit } from 'models/VocabularyModel';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { tabsOpened } from '../../../../store/actions/tabs';
import { useDispatch } from 'react-redux';
import { IRefIds } from '../../../../constants/refs';
import ModuleContext from '../../../../CMFW/context/ModuleContext';
import { GenderPairContext } from './GenderPairContext';

export type IProps = {
  item: IVocTreeEdit;
  onUnlinkChild?: (childId: number) => void;
  onUnlinkSynonym?: (synonymId: number) => void;
  onUnlinkSelf?: () => void;
  isRoot?: boolean;
};

const VocTreeNodeItem: React.FC<IProps> = (props) => {
  const dispatch = useDispatch();
  const module = useContext(ModuleContext);
  const genderPairCtx = useContext(GenderPairContext);

  const handleIdClick = (item: IVocTreeEdit) => {
    if (!item.current) {
      dispatch(
        tabsOpened({
          refId: IRefIds.vocs,
          search: item.id.toString(),
          courseId: module.courseId,
        }),
      );
    }
  };

  const renderCard = (item: IVocTreeEdit, synonym = false, isLast = false) => {
    // Show close button for:
    // 1. Synonyms: always show (ignore isRoot) - synonyms are always unlinkable
    // 2. Main item current + not root: show to unlink from parent
    // 3. Main item not current + not root: show to unlink child
    const showCloseButton =
      // For synonyms: always show if callback exists (ignore isRoot)
      (synonym && !item.current && props.onUnlinkSynonym) ||
      // For main item: only show if not root
      (!synonym && !props.isRoot && ((item.current && props.onUnlinkSelf) || (!item.current && props.onUnlinkChild)));

    // Determine tooltip text based on action
    let buttonTitle = '';
    if (item.current && props.onUnlinkSelf) {
      buttonTitle = 'Unlink from parent';
    } else if (synonym) {
      buttonTitle = 'Unlink synonym';
    } else {
      buttonTitle = 'Unlink child';
    }

    const handleClose = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (item.current && props.onUnlinkSelf) {
        // Unlink current item from its parent
        props.onUnlinkSelf();
      } else if (synonym && props.onUnlinkSynonym) {
        // Unlink synonym child
        props.onUnlinkSynonym(item.id);
      } else if (!synonym && props.onUnlinkChild) {
        // Unlink regular child
        props.onUnlinkChild(item.id);
      }
    };

    const gender = item.gender;
    const genderPair = item.genderPair;
    const hasGenderIcon = gender !== null && genderPair !== null;
    const pairInSameTree = genderPair !== null && genderPairCtx.treeIds.has(genderPair.id);
    const glow = !!genderPairCtx.hoveredIds && genderPairCtx.hoveredIds.includes(item.id);

    const handleGenderEnter = () => {
      // Reciprocal highlight only makes sense when the pair is on screen.
      if (genderPair !== null && pairInSameTree) {
        genderPairCtx.setHoveredIds([item.id, genderPair.id]);
      }
    };

    const handleGenderLeave = () => {
      if (hasGenderIcon && pairInSameTree) {
        genderPairCtx.setHoveredIds(null);
      }
    };

    const handleGenderClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      // Clicking the gender symbol loads the paired voc directly.
      if (genderPair !== null) {
        dispatch(
          tabsOpened({
            refId: IRefIds.vocs,
            search: genderPair.id.toString(),
            courseId: module.courseId,
          }),
        );
      }
    };

    return (
      <Wrapper
        $current={item.current}
        $synonym={synonym}
        $isLast={isLast}
        $glow={glow}
        $glowGender={item.gender}
        key={item.id}
        onMouseEnter={handleGenderEnter}
        onMouseLeave={handleGenderLeave}
      >
        {showCloseButton && (
          <CloseButton $shifted={hasGenderIcon} onClick={handleClose} title={buttonTitle}>
            <i className="fas fa-times" />
          </CloseButton>
        )}
        {gender !== null && genderPair !== null && (
          <GenderCorner
            $gender={gender}
            onClick={handleGenderClick}
            title={`Open gender pair: ${genderPair.title} (#${genderPair.id})`}
          >
            <i className={gender === 'm' ? 'fas fa-mars' : 'fas fa-venus'} />
          </GenderCorner>
        )}
        <ID $current={item.current} onClick={() => handleIdClick(item)}>
          {item.id}
        </ID>
        <Title>{item.title}</Title>
      </Wrapper>
    );
  };

  const hasSynonyms = props.item.synonyms.length > 0;

  return (
    <>
      {renderCard(props.item, false, !hasSynonyms)}
      {props.item.synonyms.map((synonym, index) => renderCard(synonym, true, index === props.item.synonyms.length - 1))}
    </>
  );
};

export default VocTreeNodeItem;

const glowColor = (gender: 'm' | 'f' | null) => (gender === 'm' ? '#1e88e5' : '#d81b60');

const Wrapper = styled.div<{
  $current: boolean;
  $synonym: boolean;
  $isLast: boolean;
  $glow: boolean;
  $glowGender: 'm' | 'f' | null;
}>`
  font-size: 12px;
  width: 160px;
  box-sizing: border-box;
  border: solid 1px ${(props) => (props.$glow ? glowColor(props.$glowGender) : props.$current ? '#81c784' : '#d0d0d4')};
  box-shadow: ${(props) => (props.$glow ? `0 0 0 2px ${glowColor(props.$glowGender)}55` : 'none')};
  position: relative;
  padding: 10px 2px;
  white-space: normal;
  margin-left: auto;
  margin-right: auto;
  background-color: ${(props) => (props.$current ? '#e8f5e9' : '#ffffff')};
  border-top-width: ${(props) => (props.$synonym ? 0 : 1)}px;
  border-radius: ${(props) => {
    if (!props.$synonym && props.$isLast) return '6px'; // Main item without synonyms
    if (!props.$synonym && !props.$isLast) return '6px 6px 0 0'; // Main item with synonyms
    if (props.$synonym && props.$isLast) return '0 0 6px 6px'; // Last synonym
    return '0'; // Middle synonym
  }};
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

  &:hover {
    border-color: ${(props) => (props.$glow ? glowColor(props.$glowGender) : props.$current ? '#66bb6a' : '#b0b0b8')};

    button {
      opacity: 1;
    }
  }
`;

const Title = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #202124;
  line-height: 1.4;
  word-wrap: break-word;
`;

const ID = styled.p<{ $current: boolean }>`
  margin: 0;
  text-decoration: ${(props) => (props.$current ? 'none' : 'underline')};
  cursor: ${(props) => (props.$current ? 'auto' : 'pointer')};
  font-size: 12px;
  color: #202124;
`;

const GenderCorner = styled.button<{ $gender: 'm' | 'f' }>`
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  margin: 0;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  z-index: 2;
  color: ${(props) => (props.$gender === 'm' ? '#1e88e5' : '#d81b60')};
  transition: transform 0.12s ease-in-out;

  &:hover {
    transform: scale(1.15);
  }
`;

const CloseButton = styled.button<{ $shifted?: boolean }>`
  position: absolute;
  top: 6px;
  /* When a gender icon sits top-right, move the unlink × to the top-left so the two
     controls don't read as a pair (× is unlink-from-tree, not "remove gender"). */
  ${(props) => (props.$shifted ? 'left: 6px;' : 'right: 6px;')}
  background-color: rgba(95, 99, 104, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  opacity: 0;
  transition: all 0.2s ease-in-out;
  color: #5f6368;
  font-size: 11px;

  &:hover {
    background-color: #ef5350;
    color: #ffffff;
  }
`;
